import React, { useState, useEffect, useContext } from "react";
import { baseURL } from "../../services/api";
import OrgContext from "../../organisation/OrgContext";
import jwtInterceoptor from "../../components/shared/jwtInterceptor";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import useAuthHelper from "../../helpers/AuthHelper";
import AuthContext from "../../components/shared/AuthContext";
import Select from "react-select";
import { exportHistoryToPDF, exportCurrentPageToPDF } from "./exportPdf"; 

const HistoryListe = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isExporting, setIsExporting] = useState(false); // État pour l'export

  // Récupération du contexte de l'organisation
  const [t, i18n] = useTranslation();
  const { organisation } = useContext(OrgContext);
  const { onlyAdmins } = useAuthHelper();
  const { user } = useContext(AuthContext);
  // Importation des hooks nécessaires
  useEffect(() => {
    onlyAdmins(user);
    getTeamMembers();
  }, []);
  const getTeamMembers = async () => {
    setLoading(true);
    try {
      const response = await jwtInterceoptor.get(`/data/team/list/${user.id}`);
      if (response.status === 200 && response.data && response.data.data) {
        setTeamMembers(response.data.data);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      toast.error(t("Failed to load team members"));
    }
    setLoading(false);
  };

  // État pour les filtres
  const [filters, setFilters] = useState({
    user_id: "",
    action_type: "",
    entity_type: "",
    start_date: "",
    end_date: "",
  });

  // État pour la pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  useEffect(() => {
    if (organisation?.id) {
      fetchHistory();
    }
  }, [filters, organisation?.id]); // Recharger les données lorsque les filtres changent

  // Fetch history data from the API
  const fetchHistory = async () => {
    if (!organisation?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Nettoyer les filtres vides
      const cleanFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value && value.trim() !== "") {
            acc[key] = value.trim();
          }
          return acc;
        },
        {}
      );

      const params = {
        ...cleanFilters,
      };

      const response = await jwtInterceoptor.get(
        `${baseURL}/audit-trail/${organisation.id}`,
        {
          params,
        }
      );

      if (response.data.success) {
        setHistory(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          totalItems: response.data.data?.length || 0,
        }));
      } else {
        throw new Error("Réponse API invalide");
      }
    } catch (err) {
      console.error("Erreur lors du chargement:", err);
      setError(
        err.response?.data?.message || "Erreur lors du chargement des données."
      );
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };
// Fonction d'export PDF de toutes les données
const handleExportAllToPDF = async () => {
  setIsExporting(true);
  try {
    const result = exportHistoryToPDF(history, filters, organisation?.name);
    if (result.success) {
      toast.success(`Export PDF réussi: ${result.fileName}`);
    } else {
      toast.error(result.message);
    }
  } catch (error) {
    toast.error("Erreur lors de l'export PDF");
    console.error("Export error:", error);
  } finally {
    setIsExporting(false);
  }
};

// Fonction d'export PDF de la page courante
const handleExportCurrentPageToPDF = async () => {
  setIsExporting(true);
  try {
    const result = exportCurrentPageToPDF(
      currentItems, 
      pagination, 
      filters, 
      organisation?.name
    );
    if (result.success) {
      toast.success(`Export PDF réussi: ${result.fileName}`);
    } else {
      toast.error(result.message);
    }
  } catch (error) {
    toast.error("Erreur lors de l'export PDF");
    console.error("Export error:", error);
  } finally {
    setIsExporting(false);
  }
};
  // Gestion des changements dans les filtres
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      user_id: "",
      action_type: "",
      entity_type: "",
      start_date: "",
      end_date: "",
    });
  };

  // Formatage de la date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Pagination logic
  const indexOfLastItem = pagination.currentPage * pagination.itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - pagination.itemsPerPage;
  const currentItems = history.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(history.length / pagination.itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: pageNumber,
    }));
  };

  if (!organisation?.id) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          Aucune organisation sélectionnée. Veuillez sélectionner une
          organisation.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <h1 className="mb-3">Historique des actions</h1>
          <p className="text-muted mb-4">
            Cette page affiche l'historique des actions effectuées dans
            l'application pour l'organisation:{" "}
            <strong>{organisation.name}</strong>
          </p>
          {/* Boutons d'export */}
          <div className="btn-group">
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={handleExportAllToPDF}
                disabled={isExporting || history.length === 0}
              >
                {isExporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Export...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-pdf me-2"></i>
                    Exporter tout en PDF
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleExportCurrentPageToPDF}
                disabled={isExporting || currentItems.length === 0}
              >
                {isExporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Export...
                  </>
                ) : (
                  <>
                    <i className="fas fa-file-pdf me-2"></i>
                    Exporter page courante
                  </>
                )}
              </button>
            </div>
          <hr />
          {/* Formulaire de filtrage */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Filtres de recherche</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6 col-lg-4">
                  <label htmlFor="user_id" className="form-label">
                    ID Utilisateur
                  </label>
                  
                  <Select
                    options={teamMembers
                      .filter((member) => {
                        // const orgs = member.orgs.split(",");
                         const orgs = (member.orgs || "").split(",");
                        return orgs.includes(organisation.id.toString());
                      })
                      .map((member) => ({
                        value: member.name,
                        label: `${member.name} (${member.email})`,
                      }))}
                    id="user_id"
                    name="user_id"
                    placeholder="Rechercher par utilisateur"
                    value={
                      filters.user_id
                        ? {
                            value: filters.user_id,
                            label:
                              teamMembers.find(
                                (member) => member.name === filters.user_id
                              )?.name || "Utilisateur inconnu",
                          }
                        : null
                    }
                    onChange={(option) =>
                      setFilters((prev) => ({
                        ...prev,
                        user_id: option ? option.value : "",
                      }))
                    }
                    isClearable
                    isSearchable
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <label htmlFor="action_type" className="form-label">
                    Type d'action
                  </label>
                  {/* <input
                    type="text"
                    id="action_type"
                    name="action_type"
                    className="form-control"
                    placeholder="Ex: CREATE, UPDATE, DELETE"
                    value={filters.action_type}
                    onChange={handleFilterChange}
                  /> */}
                  <Select
                    options={[
                      { value: "CREATE", label: "Créer" },
                      { value: "UPDATE", label: "Modifier" },
                      { value: "DELETE", label: "Supprimer" },
                    ]}
                    id="action_type"
                    name="action_type"
                    placeholder="Sélectionner un type d'action"
                    value={
                      filters.action_type
                        ? {
                            value: filters.action_type,
                            label:
                              [
                                { value: "CREATE", label: "Créer" },
                                { value: "UPDATE", label: "Modifier" },
                                { value: "DELETE", label: "Supprimer" },
                                { value: "LOGIN", label: "Connexion" },
                                { value: "LOGOUT", label: "Déconnexion" },
                              ].find(
                                (option) =>
                                  option.value === filters.action_type
                              )?.label || "Type d'action inconnu",
                          }
                        : null
                    }
                    onChange={(option) =>
                      setFilters((prev) => ({
                        ...prev,
                        action_type: option ? option.value : "",
                      }))
                    }
                    isClearable
                    isSearchable                  
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <label htmlFor="start_date" className="form-label">
                    Date de début
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    className="form-control"
                    value={filters.start_date}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4">
                  <label htmlFor="end_date" className="form-label">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    className="form-control"
                    value={filters.end_date}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-6 col-lg-4 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={resetFilters}
                  >
                    Réinitialiser
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={fetchHistory}
                    disabled={loading}
                  >
                    {loading ? "Recherche..." : "Rechercher"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contenu principal */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-2">Chargement des données...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              <h6>Erreur de chargement</h6>
              {error}
              <button
                className="btn btn-outline-danger btn-sm mt-2"
                onClick={fetchHistory}
              >
                Réessayer
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="alert alert-info text-center">
              <h6>Aucune donnée trouvée</h6>
              <p className="mb-0">
                Aucune action enregistrée ne correspond aux critères de
                recherche.
              </p>
            </div>
          ) : (
            <>
              {/* Informations de pagination */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="mb-0 text-muted">
                  Affichage de {indexOfFirstItem + 1} à{" "}
                  {Math.min(indexOfLastItem, history.length)} sur{" "}
                  {history.length} entrées
                </p>
                <div className="d-flex align-items-center">
                  <span className="me-2">Éléments par page:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={pagination.itemsPerPage}
                    onChange={(e) =>
                      setPagination((prev) => ({
                        ...prev,
                        itemsPerPage: parseInt(e.target.value),
                        currentPage: 1,
                      }))
                    }
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Tableau des données */}
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-striped table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Utilisateur</th>
                          <th>Action</th>
                          <th>Entité</th>
                          <th>ID Entité</th>
                          <th>Adresse IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((item) => (
                          <tr key={item.id}>
                            <td className="text-nowrap">
                              {formatDate(item.created_at || item.date)}
                            </td>
                            <td>
                              <span className="badge bg-secondary">
                                {item.name || "system"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  item.action_type === "CREATE" ||
                                  item.action === "CREATE"
                                    ? "bg-success"
                                    : item.action_type === "UPDATE" ||
                                      item.action === "UPDATE"
                                    ? "bg-warning"
                                    : item.action_type === "DELETE" ||
                                      item.action === "DELETE"
                                    ? "bg-danger"
                                    : "bg-info"
                                }`}
                              >
                                {item.action_type === "CREATE" ||
                                item.action === "CREATE"
                                  ? "Créer"
                                  : item.action_type === "UPDATE" ||
                                    item.action === "UPDATE"
                                  ? "Modifier"
                                  : item.action_type === "DELETE" ||
                                    item.action === "DELETE"
                                  ? "Supprimer"
                                  : "Action inconnue"}
                              </span>
                            </td>
                            <td>{item.entity_type || item.entite || "-"}</td>
                            <td className="font-monospace">
                              {item.entity_id || item.entite_id || "-"}
                            </td>
                            <td className="font-monospace small">
                              {item.ip_address || item.adresse_ip || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <nav className="mt-4">
                  <ul className="pagination justify-content-center">
                    <li
                      className={`page-item ${
                        pagination.currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage - 1)
                        }
                        disabled={pagination.currentPage === 1}
                      >
                        Précédent
                      </button>
                    </li>

                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Afficher seulement quelques pages autour de la page courante
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= pagination.currentPage - 2 &&
                          pageNumber <= pagination.currentPage + 2)
                      ) {
                        return (
                          <li
                            key={pageNumber}
                            className={`page-item ${
                              pagination.currentPage === pageNumber
                                ? "active"
                                : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        );
                      } else if (
                        pageNumber === pagination.currentPage - 3 ||
                        pageNumber === pagination.currentPage + 3
                      ) {
                        return (
                          <li key={pageNumber} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}

                    <li
                      className={`page-item ${
                        pagination.currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          handlePageChange(pagination.currentPage + 1)
                        }
                        disabled={pagination.currentPage === totalPages}
                      >
                        Suivant
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryListe;
